import { jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from "react";
import videojs from "video.js";
import "video.js/dist/video-js.css";
import "./VideoPlayer.css";
import { useTranslation } from "react-i18next";
import { ContentType } from "../../types/global";
import { VideoPlayerConfig, videoPlayerConfig, } from "./VideoPlayerType";
import { getAutoplay } from "../../hooks/useAutoplay";
import { canAutoplayWithSound } from "../../lib/autoplay";
import { getFillerGif, getGroupAds } from "../../stores/useGroupStore";
import { Ads } from "../ads/Ads";
import { resolveVodAdsCustomization } from "../ads/adsStrategy";
import { LIB_PLAYER, VIDEO_TYPE, ADS_ROLL_TYPE } from "@constants";
import { isSmartTV } from "react-device-detect";
import useTailwindBreakpoint from "../../hooks/useTailwindBreakpoint";
/**
 * Normalizzo l'url come fa il browser (spazi/a capo tolti, url assoluto), cosi' il confronto
 * con player.currentSrc() non da' falsi "diversi" che ricaricherebbero il video
 */
function normalizeSourceUrl(url) {
    const cleanUrl = String(url ?? "").trim();
    try {
        return new URL(cleanUrl, window.location.href).href;
    }
    catch {
        return cleanUrl;
    }
}
export default function VideoPlayer({ landscape, rel, data, videoPlayerInfo, videoPlayerMetadata, config = VideoPlayerConfig.Default, options, captions, redirectOnVideoEnded, onReady, onEnded, groupsPlayerData, onAdBlockDetected, playerClass, }) {
    const isLg = useTailwindBreakpoint("lg");
    const { t } = useTranslation();
    // ==========================================================
    // CONTENT REL
    // ==========================================================
    // ==========================================================
    // VIDEO.JS REFS
    // ==========================================================
    /**
     * Container React nel quale Video.js creerà
     * dinamicamente il proprio elemento <video-js>.
     */
    const videoContainerRef = React.useRef(null);
    /**
     * Mantiene internamente l'istanza di Video.js.
     *
     * Il ref NON provoca un nuovo render quando viene modificato.
     *
     * Viene utilizzato principalmente per:
     *
     * - accedere direttamente all'istanza del player;
     * - verificare se il player è già stato inizializzato;
     * - aggiornare source/options;
     * - eseguire dispose() nel cleanup.
     */
    const playerRef = React.useRef(null);
    /**
     * Mantiene l'istanza di Video.js nello stato React.
     *
     * A differenza di `playerRef`, l'aggiornamento tramite
     * `setPlayer()` provoca un nuovo render del componente.
     *
     * Questo permette di montare componenti che dipendono
     * dall'esistenza del player, ad esempio <Ads />, solamente
     * dopo che Video.js è stato inizializzato ed è disponibile.
     *
     * Flusso:
     *
     * 1. inizialmente `player` è null;
     * 2. Video.js viene inizializzato;
     * 3. quando Video.js è pronto viene chiamato setPlayer(player);
     * 4. React esegue un nuovo render;
     * 5. <Ads /> viene montato perché il player è disponibile.
     *
     * `playerRef` rimane invece utilizzato per gestire internamente
     * l'istanza senza provocare nuovi render.
     */
    const [player, setPlayer] = React.useState(null);
    /**
     * Mantiene l'istanza Ads associata al player corrente.
     * Il ref serve per eseguire destroy() nel cleanup senza ricreare il manager
     * durante i render React.
     */
    const adsManagerRef = React.useRef(null);
    // ==========================================================
    // CONFIGURAZIONE PLAYER
    // ==========================================================
    /**
     * Recupera il preset selezionato.
     */
    const configData = videoPlayerConfig[config];
    /**
     * Le callback passate direttamente al componente
     * hanno priorità rispetto a quelle definite nel preset.
     */
    const finalOnReady = onReady ?? configData.onReady;
    const finalOnEnded = onEnded ?? configData.onEnded;
    /**
     * Conserviamo le callback dentro ref.
     *
     * Questo evita di dover distruggere e ricreare Video.js
     * solamente perché React ha creato una nuova funzione
     * durante un render.
     */
    const onReadyRef = React.useRef(finalOnReady);
    const onEndedRef = React.useRef(finalOnEnded);
    React.useEffect(() => {
        onReadyRef.current = finalOnReady;
    }, [finalOnReady]);
    React.useEffect(() => {
        onEndedRef.current = finalOnEnded;
    }, [finalOnEnded]);
    const adsGroup = getGroupAds();
    const fillerGif = getFillerGif();
    /**
     * Video.js rimane il player di default, mantenendo il comportamento attuale.
     * Passando groupsPlayerData è possibile selezionare Brid con gli stessi
     * identificativi utilizzati dal vecchio Player.js.
     */
    const finalGroupsPlayerData = React.useMemo(() => groupsPlayerData ?? {
        groupsPlayer: LIB_PLAYER.VIDEO_JS,
        groupsBridID: null,
        bridPlayerID: null,
    }, [groupsPlayerData]);
    const contentType = data?.content?.type;
    const videoUrl = videoPlayerInfo?.source_url;
    /**
     * Costruisce i parametri necessari al componente Ads.
     *
     * La configurazione VAST e le personalizzazioni advertising
     * cambiano in base alla tipologia del contenuto:
     *
     * - Livestream / Fastchannel -> configurazione Live
     * - Video -> configurazione VOD
     *
     * Il risultato viene successivamente passato al componente <Ads />.
     */
    const adsParams = React.useMemo(() => {
        if (!adsGroup || !data) {
            return null;
        }
        const adsMetadata = data.ads_metadata ?? null;
        /**
         * Seleziona i tag VAST corretti in base
         * alla tipologia del contenuto.
         */
        const tagVast = contentType === ContentType.Livestream ||
            contentType === ContentType.Fastchannels
            ? adsGroup.tag_vast?.live_tag_vast
            : adsGroup.tag_vast?.video_tag_vast;
        /**
         * Seleziona la configurazione Ads corretta
         * in base alla tipologia del contenuto.
         */
        // Per i VOD la pubblicita' personalizzata del singolo contenuto (info.ads_data) vince sul gruppo
        const adsCustomization = contentType === ContentType.Livestream ||
            contentType === ContentType.Fastchannels
            ? adsGroup.ads_customization?.live_ads_customization
            : resolveVodAdsCustomization(adsGroup.ads_customization?.video_ads_customization, videoPlayerInfo?.ads_data);
        return {
            tagVast,
            adsCustomization,
            fillerGif,
            addrForTeads: adsGroup.ads_customization?.addr_for_teads,
            groupsPlayerData: finalGroupsPlayerData,
            groupsAds: {
                autopromotions: adsGroup.autopromotions ?? [],
                lanners: adsGroup.lanners ?? [],
            },
            advMetadata: adsMetadata,
            relFileId: rel,
            videoType: contentType === ContentType.Livestream ||
                contentType === ContentType.Fastchannels
                ? VIDEO_TYPE.LIVE
                : VIDEO_TYPE.VOD,
            isAdsPlayingStatus: data.is_ads_playing ?? false,
            needFullscreen: false,
            duration: Number(videoPlayerMetadata?.duration ?? 0) || 0,
            onContentEnded: () => onEndedRef.current?.(),
            onAdBlockDetected,
        };
    }, [
        adsGroup,
        data,
        contentType,
        fillerGif,
        finalGroupsPlayerData,
        rel,
        videoPlayerMetadata,
        videoPlayerInfo?.ads_data,
        onAdBlockDetected,
    ]);
    /**
     * Replica il controllo checkADS() del Player.js legacy.
     * Serve a decidere se costruire solamente il player oppure inizializzare
     * anche l'orchestratore Ads.
     */
    const adsEnabled = React.useMemo(() => {
        if (!adsParams?.adsCustomization || !data.ads_status || isSmartTV) {
            return false;
        }
        // Uso Number() perche' gli status possono arrivare anche come stringa "1"
        if (adsParams.videoType === VIDEO_TYPE.LIVE) {
            const adsCustomization = adsParams.adsCustomization;
            return (Number(adsCustomization.live_pre_roll_status ?? 0) === 1 ||
                Number(adsCustomization.live_mid_roll_type ?? 0) > 0);
        }
        const adsCustomization = adsParams.adsCustomization;
        return (Number(adsCustomization.vod_pre_roll_status ?? 0) === 1 ||
            Number(adsCustomization.vod_post_roll_status ?? 0) === 1 ||
            Number(adsCustomization.vod_mid_roll_type ?? 0) > 0);
    }, [adsParams]);
    // ==========================================================
    // CAPTIONS
    // ==========================================================
    /**
     * Le captions passate direttamente al componente
     * hanno priorità rispetto a quelle ricevute dall'API.
     */
    const finalCaptions = React.useMemo(() => captions ?? videoPlayerInfo?.captions ?? [], [captions, videoPlayerInfo?.captions]);
    // ==========================================================
    // FINAL VIDEO.JS OPTIONS
    // ==========================================================
    /**
     * Copertina del contenuto: video.js la mostra al posto del nero finche' l'utente non preme play,
     * e il loader del pre-roll la usa come sfondo
     */
    const posterUrl = videoPlayerMetadata?.image?.landscape?.trim() || undefined;
    const finalOptions = React.useMemo(() => {
        if (!videoUrl) {
            return null;
        }
        const source = {
            src: videoUrl,
            type: config === VideoPlayerConfig.Live
                ? "application/x-mpegURL"
                : "video/mp4",
        };
        const runtimeOptions = {
            isMobile: !isLg,
            caption: finalCaptions,
        };
        if (config === VideoPlayerConfig.Default) {
            runtimeOptions.redirectOnVideoEnded =
                redirectOnVideoEnded ?? getAutoplay();
        }
        return {
            ...configData.options,
            ...runtimeOptions,
            ...options,
            html5: {
                ...configData.options?.html5,
                ...options?.html5,
                vhs: {
                    ...configData.options?.html5?.vhs,
                    ...options?.html5?.vhs,
                    useCueTags: true,
                },
            },
            aspectRatio: Boolean(Number(landscape.land_cinema_status))
                ? undefined
                : (options?.aspectRatio ?? configData.options.aspectRatio),
            sources: options?.sources ?? [source],
            poster: options?.poster ?? posterUrl,
            // non e' un'opzione di video.js: la legge il loader del pre-roll ("Poi parte <titolo>")
            contentTitle: videoPlayerMetadata?.title ?? "",
            // Niente autoplay ne' per le live ne' per i VOD: il contenuto (e le ads) partono
            // solo dal primo gesto dell'utente
            autoplay: false,
            muted: false,
        };
    }, [
        videoUrl,
        config,
        configData,
        options,
        finalCaptions,
        redirectOnVideoEnded,
        isLg,
        posterUrl,
        videoPlayerMetadata?.title,
    ]);
    // ==========================================================
    // ENDED
    // ==========================================================
    /**
     * Handler stabile dell'evento ended.
     *
     * Legge la callback reale da onEndedRef,
     * evitando di registrare continuamente nuovi listener.
     */
    const handleEnded = React.useCallback(() => {
        onEndedRef.current?.();
    }, []);
    // ==========================================================
    // VIDEO.JS INIT / UPDATE
    // ==========================================================
    React.useEffect(() => {
        if (!videoContainerRef.current || !finalOptions || !adsParams) {
            return;
        }
        const playerTypeToBuild = finalGroupsPlayerData.groupsPlayer;
        // ========================================================
        // PRIMA INIZIALIZZAZIONE
        // ========================================================
        if (!playerRef.current) {
            /**
             * Creiamo sempre un elemento video sorgente. Video.js lo utilizza
             * direttamente; Brid lo usa per recuperare la source prima di
             * sostituirlo con il proprio container, come avveniva in Player.js.
             */
            const videoElement = document.createElement("video");
            videoElement.id = "videoPlayerBab";
            videoElement.setAttribute("crossorigin", "anonymous");
            // videoElement.src = String(
            //   finalOptions.sources?.[0]?.src ?? videoUrl ?? "",
            // );
            videoContainerRef.current.appendChild(videoElement);
            // ------------------------------------------------------
            // CREATE BRID PLAYER
            // ------------------------------------------------------
            if (playerTypeToBuild === LIB_PLAYER.BRID_PLAYER) {
                const groupsBridPlayer = finalGroupsPlayerData.bridPlayerID;
                const bridCode = finalGroupsPlayerData.groupsBridID;
                const videoUrl = videoElement.currentSrc || videoElement.src;
                const width = "16";
                const height = "9";
                if (groupsBridPlayer == null || bridCode == null) {
                    console.error("Configurazione Brid incompleta: groupsBridID e bridPlayerID sono obbligatori");
                    return;
                }
                // Preparazione spazio per Brid Player
                const bridElement = document.createElement("div");
                bridElement.id = String(bridCode);
                bridElement.className = "brid brid_player";
                bridElement.setAttribute("style", "width:16;height:9;");
                videoElement.after(bridElement);
                videoElement.remove();
                // Costruzione Brid Player, in base a se sono attivati gli adv o meno
                // Per fare debug del brid player
                // Brid.debug = true;
                if (adsEnabled) {
                    $bp(String(bridCode), {
                        id: "" + groupsBridPlayer + "",
                        width: "" + width + "",
                        height: "" + height + "",
                        video: "" + videoUrl + "",
                        adSDK: ["ima"],
                    });
                }
                else {
                    $bp(String(bridCode), {
                        id: "" + groupsBridPlayer + "",
                        width: "" + width + "",
                        height: "" + height + "",
                        video: "" + videoUrl + "",
                    });
                }
                const bridPlayer = $bp();
                playerRef.current = bridPlayer;
                setPlayer(bridPlayer);
                // Quando il player è pronto...
                bridPlayer.on("ready", () => {
                    // ... ed il contenuto è una live, rimuovo i controlli di manipolazione della riproduzione
                    if (adsParams.videoType == VIDEO_TYPE.LIVE) {
                        document
                            .querySelectorAll(".brid-progress-control, .brid-rewind-control, .brid-hd-control, .brid-playlist-control, .brid-time-controls")
                            .forEach((element) => element.remove());
                    }
                    onReadyRef.current?.(bridPlayer);
                });
                if (adsEnabled) {
                    adsManagerRef.current = new Ads(bridPlayer, {
                        ...adsParams,
                        videoContent: videoElement,
                    });
                }
                else {
                    bridPlayer.play?.();
                }
                /**
                 * Nel Player.js legacy la fine contenuto era gestita fuori dalla classe
                 * Ads. Manteniamo la stessa responsabilità: se esiste un post-roll lo
                 * avviamo, altrimenti notifichiamo direttamente il chiamante React.
                 */
                bridPlayer.on("ended", () => {
                    const adsManager = adsManagerRef.current;
                    if (adsManager &&
                        adsManager.adsCustomizationPostRoll === 1 &&
                        adsParams.videoType === VIDEO_TYPE.VOD) {
                        adsManager.videoEnded = true;
                        adsManager.currentAdType = ADS_ROLL_TYPE.POST_ROLL;
                        adsManager.adsPostRoll();
                        return;
                    }
                    handleEnded();
                });
                return;
            }
            // ------------------------------------------------------
            // CREATE VIDEO.JS PLAYER
            // ------------------------------------------------------
            //LIB 04/09/2026 le classi css di tailwind sono state aggiunte sennò il player si sfonda su viewcinema
            videoElement.classList.add("video-js", "vjs-default-skin", "vjs-big-play-centered", "!w-full", "!max-w-full", "!h-full", "!max-h-full");
            console.log("SETTINGS", {
                ...finalOptions,
            });
            const videoJsPlayer = videojs(videoElement, finalOptions, () => {
                // ==================================================
                // CAPTIONS
                // ==================================================
                finalCaptions.forEach((caption, index) => {
                    videoJsPlayer.addRemoteTextTrack({
                        kind: "captions",
                        src: caption.name,
                        srclang: caption.srclang,
                        label: caption.srclang.toUpperCase(),
                        /**
                         * Il primo sottotitolo viene utilizzato
                         * come default.
                         */
                        default: index === 0,
                    }, false);
                });
                // ==================================================
                // PLAYER READY
                // ==================================================
                /**
                 * Salviamo l'istanza nello state React.
                 *
                 * Questo provoca un nuovo render e permette
                 * quindi il mount di <Ads />.
                 */
                setPlayer(videoJsPlayer);
                /**
                 * Callback esterna opzionale.
                 */
                onReadyRef.current?.(videoJsPlayer);
                /**
                 * Se l'utente ha gia' interagito con il sito (es. click dalla home o dalla barra laterale)
                 * il browser permette di partire subito con l'audio. Con le Ads attive l'avvio lo gestisce
                 * AdsVideoJs, che deve far partire prima il pre-roll.
                 */
                if (!adsManagerRef.current) {
                    void canAutoplayWithSound().then((allowed) => {
                        const readyPlayer = videoJsPlayer;
                        if (!allowed ||
                            readyPlayer.isDisposed?.() ||
                            readyPlayer.hasStarted?.()) {
                            return;
                        }
                        readyPlayer.play()?.catch?.(() => {
                            // il browser ha cambiato idea: resta il big play
                        });
                    });
                }
                videojs.log("Video.js ready");
            });
            /**
             * Salviamo immediatamente anche l'istanza
             * nel ref interno.
             */
            playerRef.current = videoJsPlayer;
            /**
             * IMPORTANTE:
             * contrib-ads / IMA devono essere inizializzati
             * immediatamente dopo la creazione di Video.js,
             * prima dell'evento loadstart.
             */
            if (adsEnabled) {
                try {
                    adsManagerRef.current = new Ads(videoJsPlayer, {
                        ...adsParams,
                        videoContent: videoElement,
                        videoUrl: videoUrl,
                    });
                }
                catch (error) {
                    // Se la parte Ads non si inizializza il contenuto deve comunque essere guardabile
                    console.error("Errore inizializzazione Ads:", error);
                    adsManagerRef.current = null;
                }
            }
            if (!adsManagerRef.current) {
                /**
                 * Senza Ads l'evento ended rimane gestito direttamente dal player.
                 * Con Ads, invece, AdsVideoJs gestisce post-roll/autopromotions e
                 * richiama onContentEnded solo quando l'intero flusso è concluso.
                 */
                videoJsPlayer.on("ended", handleEnded);
            }
            // Appena il player è inizializzato
            // forza la modalità di adattamento al container padre
            // a prescindere da come è stato creato il player
            videoJsPlayer.fluid(false);
            videoJsPlayer.fill(true);
            return;
        }
        // ========================================================
        // PLAYER GIÀ INIZIALIZZATO
        // ========================================================
        const currentPlayer = playerRef.current;
        if (playerTypeToBuild === LIB_PLAYER.VIDEO_JS) {
            /**
             * Aggiorna la source solamente se è cambiata davvero.
             * Durante un break (ad IMA o autopromo) non la tocco mai: cambiare src li' dentro
             * interrompe l'ad e fa resettare lo stato a contrib-ads.
             */
            const nextSource = finalOptions.sources?.[0];
            const videoJsPlayer = currentPlayer;
            if (nextSource?.src &&
                !adsManagerRef.current?.isBreakActive() &&
                !videoJsPlayer.ads?.inAdBreak?.() &&
                normalizeSourceUrl(videoJsPlayer.currentSrc()) !==
                    normalizeSourceUrl(nextSource.src)) {
                videoJsPlayer.src(finalOptions.sources);
            }
        }
    }, [
        finalOptions,
        finalCaptions,
        handleEnded,
        adsParams,
        adsEnabled,
        finalGroupsPlayerData,
        videoUrl,
    ]);
    // ==========================================================
    // CLEANUP PLAYER
    // ==========================================================
    React.useEffect(() => {
        return () => {
            const currentPlayer = playerRef.current;
            setPlayer(null);
            adsManagerRef.current?.destroy();
            adsManagerRef.current = null;
            if (currentPlayer) {
                if (finalGroupsPlayerData.groupsPlayer === LIB_PLAYER.VIDEO_JS) {
                    const videoJsPlayer = currentPlayer;
                    if (!videoJsPlayer.isDisposed?.()) {
                        videoJsPlayer.off("ended", handleEnded);
                        /**
                         * Video.js dispose:
                         *
                         * - rimuove listener;
                         * - rimuove DOM;
                         * - distrugge i componenti;
                         * - libera le risorse.
                         */
                        videoJsPlayer.dispose();
                    }
                }
                else {
                    /**
                     * Brid non espone la stessa API dispose di Video.js in tutte le
                     * versioni. Usiamo quindi il metodo disponibile sull'istanza.
                     */
                    currentPlayer.off?.();
                    currentPlayer.destroy?.();
                    currentPlayer.dispose?.();
                }
            }
            playerRef.current = null;
        };
    }, [handleEnded, finalGroupsPlayerData.groupsPlayer]);
    // ==========================================================
    // VIDEO UNAVAILABLE
    // ==========================================================
    if (!videoUrl) {
        return (_jsx("div", { className: `${playerClass} bg-black`, children: t("video-unavailable") }));
    }
    // ==========================================================
    // RENDER
    // ==========================================================
    return (_jsx(_Fragment, { children: _jsx("div", { className: playerClass, children: _jsx("div", { ref: videoContainerRef, "data-vjs-player": true, className: "h-full w-full" }) }) }));
}
